/**
 * Earcons for an audio dialog, on one AudioContext unlocked by `primeListenCue`
 * during the click that starts the dialog.
 *
 * The turn cue finishes before the microphone opens, so recognition does not
 * transcribe it. The thinking blip is a short two-note cue that loops only while
 * the assistant is still searching.
 */

const FIRST_HZ = 880;
const SECOND_HZ = 1175;
const NOTE_SECONDS = 0.09;
const CUE_SECONDS = NOTE_SECONDS * 2;
const PEAK_GAIN = 0.08;
/** Opens the mic even if the oscillator never reports that it ended. */
const SAFETY_MS = CUE_SECONDS * 1000 + 80;

/** Soft blip while the assistant is thinking. Louder than a faint tick so it
 * stays audible after speech, and still a short cue rather than another line.
 */
const THINKING_FIRST_HZ = 740;
const THINKING_SECOND_HZ = 988;
const THINKING_NOTE_SECONDS = 0.12;
const THINKING_FIRST_GAIN = 0.1;
const THINKING_SECOND_GAIN = 0.08;
/** Silence between thinking blips, measured from the start of each pair. */
export const THINKING_CUE_INTERVAL_MS = 1800;
/** Leaves the speech-synthesis callback before the first blip is scheduled. */
export const THINKING_CUE_LEAD_MS = 60;

let context: AudioContext | null = null;
let contextCtor: typeof AudioContext | undefined;
let thinkingActive = false;
let thinkingTimer: number | null = null;
let thinkingNodes: Array<{ osc: OscillatorNode; gain: GainNode }> = [];

function audioConstructor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

function getAudioContext(): AudioContext | null {
  const Ctor = audioConstructor();
  if (Ctor === undefined) {
    return null;
  }
  if (context !== null && contextCtor === Ctor) {
    return context;
  }
  try {
    context = new Ctor();
    contextCtor = Ctor;
  } catch {
    context = null;
    contextCtor = undefined;
    return null;
  }
  return context;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function schedulePulse(
  ctx: AudioContext,
  frequency: number,
  when: number,
  noteSeconds: number,
  peakGain: number,
): { osc: OscillatorNode; gain: GainNode } {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(frequency, when);
  const attack = 0.012;
  const release = 0.03;
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(peakGain, when + attack);
  gain.gain.setValueAtTime(peakGain, when + noteSeconds - release);
  gain.gain.linearRampToValueAtTime(0, when + noteSeconds);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(when);
  osc.stop(when + noteSeconds);
  return { osc, gain };
}

function scheduleNote(ctx: AudioContext, frequency: number, when: number): OscillatorNode {
  return schedulePulse(ctx, frequency, when, NOTE_SECONDS, PEAK_GAIN).osc;
}

function clearThinkingTimer(): void {
  if (thinkingTimer === null) {
    return;
  }
  window.clearTimeout(thinkingTimer);
  thinkingTimer = null;
}

function stopThinkingOsc(): void {
  const nodes = thinkingNodes;
  thinkingNodes = [];
  for (const node of nodes) {
    if (context !== null) {
      try {
        const now = context.currentTime;
        node.gain.gain.cancelScheduledValues(now);
        node.gain.gain.setValueAtTime(0, now);
        node.gain.disconnect();
      } catch {
        // The context is already closed.
      }
    }
    try {
      node.osc.stop();
    } catch {
      // stop() was already scheduled for the end of this blip.
    }
  }
}

function scheduleThinkingPulse(ctx: AudioContext): void {
  const when = ctx.currentTime;
  const first = schedulePulse(
    ctx,
    THINKING_FIRST_HZ,
    when,
    THINKING_NOTE_SECONDS,
    THINKING_FIRST_GAIN,
  );
  const second = schedulePulse(
    ctx,
    THINKING_SECOND_HZ,
    when + THINKING_NOTE_SECONDS,
    THINKING_NOTE_SECONDS,
    THINKING_SECOND_GAIN,
  );
  thinkingNodes = [first, second];
  second.osc.onended = () => {
    if (thinkingNodes[1]?.osc === second.osc) {
      thinkingNodes = [];
    }
  };
}

function queueNextPulse(ctx: AudioContext): void {
  clearThinkingTimer();
  thinkingTimer = window.setTimeout(() => {
    thinkingTimer = null;
    playThinkingPulse(ctx);
  }, THINKING_CUE_INTERVAL_MS);
}

function playThinkingPulse(ctx: AudioContext): void {
  if (!thinkingActive) {
    return;
  }
  const play = () => {
    if (!thinkingActive || ctx.state !== "running") {
      return;
    }
    scheduleThinkingPulse(ctx);
    queueNextPulse(ctx);
  };
  if (ctx.state === "running") {
    play();
    return;
  }
  void ctx.resume().then(play, () => {
    thinkingActive = false;
  });
}

function scheduleCue(ctx: AudioContext, onDone: () => void): void {
  const when = ctx.currentTime;
  scheduleNote(ctx, FIRST_HZ, when);
  const last = scheduleNote(ctx, SECOND_HZ, when + NOTE_SECONDS);
  let finished = false;
  const finish = () => {
    if (finished) {
      return;
    }
    finished = true;
    window.clearTimeout(timer);
    onDone();
  };
  const timer = window.setTimeout(finish, SAFETY_MS);
  last.onended = finish;
}

/** Create or resume the shared context during the click that starts listening. */
export function primeListenCue(): void {
  const ctx = getAudioContext();
  if (ctx !== null && ctx.state === "suspended") {
    void ctx.resume();
  }
}

/**
 * Play the turn cue, then call `onDone`. `onDone` runs immediately when audio
 * is unavailable or the user prefers reduced motion, so the mic still opens.
 */
export function playListenCue(onDone: () => void): void {
  if (prefersReducedMotion()) {
    onDone();
    return;
  }
  const ctx = getAudioContext();
  if (ctx === null) {
    onDone();
    return;
  }
  if (ctx.state === "running") {
    scheduleCue(ctx, onDone);
    return;
  }
  void ctx.resume().then(
    () => {
      scheduleCue(ctx, onDone);
    },
    () => {
      onDone();
    },
  );
}

/**
 * Loop a short blip while the assistant is thinking. A second start does
 * nothing. The first blip waits out the speech callback that usually starts
 * it, and each blip resumes the shared context if speech left it suspended.
 * Reduced motion and a missing context stay silent.
 */
export function startThinkingCue(): void {
  if (thinkingActive || prefersReducedMotion()) {
    return;
  }
  const ctx = getAudioContext();
  if (ctx === null) {
    return;
  }
  thinkingActive = true;
  clearThinkingTimer();
  thinkingTimer = window.setTimeout(() => {
    thinkingTimer = null;
    playThinkingPulse(ctx);
  }, THINKING_CUE_LEAD_MS);
}

/** Stop the thinking blip. Safe when it is not playing. */
export function stopThinkingCue(): void {
  thinkingActive = false;
  clearThinkingTimer();
  stopThinkingOsc();
}
